import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Link } from '@tanstack/react-router';
import { TR_PROVINCES } from '@zt/content/tr-address';
import { Badge, Button, EmptyState, Field, Input, Section, Select, Spinner, Switch } from '@zt/ui';
import { WarningBadges } from '../components/CompanySalesPoints';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useT } from '../lib/i18n';
import { useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { SalesPointBackfill, SalesPointGeocodeRun, SalesPointList } from '../lib/types';

/**
 * Satış noktaları (F7-37, ADR-145): vitrindeki Satış Noktaları sayfasının listesi. Yeni onaylanan bayinin otomatik
 * eklenmesi (varsayılan açık), toplu göster/gizle, uyarılar (pin yok, kişi adı olabilir, bayi onaylı değil).
 * Nokta bilgileri bayi kartında düzenlenir.
 */
export function SalesPointsPage() {
  const t = useT();
  const { can } = usePermissions();
  const manage = can('b2b.dealer.manage');
  const [q, setQ] = useState('');
  const [city, setCity] = useState('');
  const [visible, setVisible] = useState<'' | 'true' | 'false'>('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const params = new URLSearchParams();
  if (q.trim().length >= 2) params.set('q', q.trim());
  if (city) params.set('city', city);
  if (visible) params.set('visible', visible);
  const list = useQuery({
    queryKey: ['sales-points', params.toString()],
    queryFn: () => api.get<SalesPointList>(`/v1/admin/sales-points?${params.toString()}`),
  });
  const settings = useApiMutation(
    (v: { autoAdd: boolean } | { geocode: boolean }) =>
      api.put<{ autoAdd: boolean; geocode: boolean }>('/v1/admin/sales-points/settings', v),
    {
      invalidate: [['sales-points']],
      onSuccess: (r, v) =>
        toast.success(
          'geocode' in v
            ? r.geocode
              ? t('admin.salesPoints.geocodeOn')
              : t('admin.salesPoints.geocodeOff')
            : r.autoAdd
              ? t('admin.salesPoints.autoAddOn')
              : t('admin.salesPoints.autoAddOff'),
        ),
    },
  );
  // Onaylı bayilerden oluştur (F7-42): noktası olmayan onaylı bayiler görünür eklenir; adresi tanınmayanlar sayılır.
  const backfill = useApiMutation(() => api.post<SalesPointBackfill>('/v1/admin/sales-points/backfill'), {
    invalidate: [['sales-points'], ['company-sales-points']],
    success: (r) =>
      t('admin.salesPoints.backfillDone', {
        created: r.created,
        companies: r.companies,
        skipped: r.skipped.length,
      }),
  });
  // Konumları şimdi bul (F7-42): en çok 10 adres OpenStreetMap'te aranır (işçi zaten on dakikada bir arar).
  const geocodeNow = useApiMutation(() => api.post<SalesPointGeocodeRun>('/v1/admin/sales-points/geocode'), {
    invalidate: [['sales-points'], ['company-sales-points']],
    success: (r) =>
      r.checked
        ? t('admin.salesPoints.geocodeNowDone', {
            checked: r.checked,
            found: r.ok + r.approximate,
            problems: r.notFound + r.mismatch,
          })
        : t('admin.salesPoints.geocodeNowNone'),
  });
  const bulk = useApiMutation(
    (v: { ids: string[]; visible: boolean }) =>
      api.post<{ changed: number }>('/v1/admin/sales-points/visibility', v),
    {
      invalidate: [['sales-points']],
      success: (r) => t('admin.salesPoints.bulkDone', { count: r.changed }),
      onSuccess: () => setSelected(new Set()),
    },
  );
  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  const chosen = items.filter((i) => selected.has(i.id)).map((i) => i.id);
  const toggle = (id: string, on: boolean) =>
    setSelected((s) => {
      const n = new Set(s);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });
  return (
    <PageFrame helpKey="page.sales_points" wide>
      <Section title={t('admin.salesPoints.settings')} action={<HelpTip k="sales_points.auto_add" />}>
        {!list.data ? (
          <Spinner label={t('common.loading')} />
        ) : (
          <div className="grid gap-4">
            <div className="flex items-start gap-3">
              <Switch
                checked={list.data.settings.autoAdd}
                disabled={!manage || settings.isPending}
                onCheckedChange={(v) => settings.mutate({ autoAdd: v })}
                label={t('admin.salesPoints.autoAdd')}
              />
              <div>
                <p className="text-sm font-medium">{t('admin.salesPoints.autoAdd')}</p>
                <p className="text-sm text-ink-3">{t('admin.salesPoints.autoAddLead')}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Switch
                checked={list.data.settings.geocode}
                disabled={!manage || settings.isPending}
                onCheckedChange={(v) => settings.mutate({ geocode: v })}
                label={t('admin.salesPoints.geocode')}
              />
              <div>
                <p className="text-sm font-medium">
                  {t('admin.salesPoints.geocode')} <HelpTip k="sales_points.geocode" />
                </p>
                <p className="text-sm text-ink-3">{t('admin.salesPoints.geocodeLead')}</p>
              </div>
            </div>
            {manage && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => backfill.mutate(undefined)}
                  disabled={backfill.isPending}
                >
                  {t('admin.salesPoints.backfill')}
                </Button>
                <HelpTip k="sales_points.backfill" />
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => geocodeNow.mutate(undefined)}
                  disabled={geocodeNow.isPending || !list.data.settings.geocode}
                >
                  {geocodeNow.isPending
                    ? t('admin.salesPoints.geocodeNowRunning')
                    : t('admin.salesPoints.geocodeNow')}
                </Button>
                <HelpTip k="sales_points.geocode_now" />
              </div>
            )}
            {backfill.data && backfill.data.skipped.length > 0 && (
              <div role="status" className="rounded-md border border-line bg-surface-2 p-3 text-sm">
                <p className="font-medium">{t('admin.salesPoints.backfillSkipped')}</p>
                <ul className="mt-1 list-disc ps-5">
                  {backfill.data.skipped.map((s) => (
                    <li key={s.companyId}>
                      <Link to="/bayiler/$id" params={{ id: s.companyId }} className="text-brand underline">
                        {s.name}
                      </Link>{' '}
                      — {t(`admin.salesPoints.missing.${s.reason}`)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Section>
      <Section title={t('admin.salesPoints.listTitle')} description={t('admin.salesPoints.listLead')}>
        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          <Field label={t('common.search')}>
            {(p) => <Input {...p} type="search" value={q} onChange={(e) => setQ(e.target.value)} />}
          </Field>
          <Field label={t('admin.dealers.city')}>
            {(p) => (
              <Select {...p} value={city} onChange={(e) => setCity(e.target.value)}>
                <option value="">{t('admin.salesPoints.allCities')}</option>
                {TR_PROVINCES.map((x) => (
                  <option key={x.code} value={x.name}>
                    {x.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('admin.salesPoints.visibility')}>
            {(p) => (
              <Select
                {...p}
                value={visible}
                onChange={(e) => setVisible(e.target.value as '' | 'true' | 'false')}
              >
                <option value="">{t('common.all')}</option>
                <option value="true">{t('admin.salesPoints.shown')}</option>
                <option value="false">{t('admin.salesPoints.hidden')}</option>
              </Select>
            )}
          </Field>
        </div>
        {manage && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              disabled={!chosen.length || bulk.isPending}
              onClick={() => bulk.mutate({ ids: chosen, visible: true })}
            >
              {t('admin.salesPoints.bulkShow', { count: chosen.length })}
            </Button>
            <Button
              size="sm"
              disabled={!chosen.length || bulk.isPending}
              onClick={() => bulk.mutate({ ids: chosen, visible: false })}
            >
              {t('admin.salesPoints.bulkHide', { count: chosen.length })}
            </Button>
            <HelpTip k="sales_points.bulk" />
          </div>
        )}
        {!list.data ? (
          <Spinner label={t('common.loading')} />
        ) : !items.length ? (
          <EmptyState title={t('admin.salesPoints.none')} description={t('admin.salesPoints.noneLead')} />
        ) : (
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full min-w-[48rem] text-sm" aria-label={t('admin.salesPoints.listTitle')}>
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  {manage && (
                    <th className="w-10 px-3 py-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-[var(--zt-pine)]"
                        aria-label={t('admin.salesPoints.selectAll')}
                        checked={chosen.length === items.length}
                        onChange={(e) =>
                          setSelected(e.target.checked ? new Set(items.map((i) => i.id)) : new Set())
                        }
                      />
                    </th>
                  )}
                  <th className="px-3 py-2 text-start font-medium">{t('admin.salesPoints.colName')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.salesPoints.colPlace')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.salesPoints.colDealer')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.salesPoints.colStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-t border-line align-top">
                    {manage && (
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-[var(--zt-pine)]"
                          aria-label={t('admin.salesPoints.selectOne', { name: p.name })}
                          checked={selected.has(p.id)}
                          onChange={(e) => toggle(p.id, e.target.checked)}
                        />
                      </td>
                    )}
                    <td className="px-3 py-2 font-medium">{p.name}</td>
                    <td className="px-3 py-2">
                      {p.district}/{p.city}
                      {p.neighbourhood && <span className="block text-ink-3">{p.neighbourhood}</span>}
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        to="/bayiler/$id"
                        params={{ id: p.company.id }}
                        className="text-pine underline underline-offset-2 hover:no-underline"
                      >
                        {p.company.name}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <span className="flex flex-wrap gap-1">
                        <Badge tone={p.visible ? 'pine' : 'neutral'}>
                          {p.visible ? t('admin.salesPoints.shown') : t('admin.salesPoints.hidden')}
                        </Badge>
                        <WarningBadges warnings={p.warnings} />
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </PageFrame>
  );
}
